import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ExternalLink, Globe2, Ticket, QrCode } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import PressShareBar from '../../components/PressShareBar';

const SITE = 'https://staymii.com';
const PAGE = '/press/staymii-launches-sgmc-nice-2026';
const DATE_ISO = '2026-08-27';
const OG_BANNER = SITE + '/og/staymii-sgmc-launch.jpg';
const HERO_IMAGE = '/og/staymii-sgmc-launch.jpg';
const GROUP_IMAGE = '/og/staymii-sgmc-group.jpg';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline: 'StayMii Launches StayMii Global Market Connect (SGMC) to Bring Malaysian Experiences to the World',
  description:
    'StayMii today launched StayMii Global Market Connect (SGMC), a new initiative aimed at helping Malaysian attractions and experience companies expand their reach into regional and international tourism markets. The launch took place at NICE 2026, KLCC, officiated by YAB Dato\u2019 Seri Anwar bin Ibrahim, Prime Minister of Malaysia.',
  datePublished: DATE_ISO,
  dateModified: DATE_ISO,
  mainEntityOfPage: SITE + PAGE,
  image: [OG_BANNER, SITE + GROUP_IMAGE],
  author: { '@type': 'Organization', name: 'StayMii', url: SITE },
  publisher: {
    '@type': 'Organization',
    name: 'StayMii',
    url: SITE,
    logo: { '@type': 'ImageObject', url: SITE + '/og-image.jpg' },
  },
  keywords:
    'StayMii Global Market Connect, SGMC launch, NICE 2026 KLCC, Anwar Ibrahim, Chang Lih Kang, MOSTI, GlobalTix MOU, Malaysia tourism technology, e-ticketing, attraction distribution',
};

const eventSchema = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: 'StayMii Global Market Connect (SGMC) Launch & MOU Signing \u2014 NICE 2026',
  startDate: DATE_ISO,
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  location: {
    '@type': 'Place',
    name: 'Kuala Lumpur Convention Centre (KLCC)',
    address: { '@type': 'PostalAddress', addressLocality: 'Kuala Lumpur', addressCountry: 'MY' },
  },
  organizer: { '@type': 'Organization', name: 'StayMii', url: SITE },
  description:
    'Launch of StayMii Global Market Connect (SGMC) and signing of a strategic Memorandum of Understanding with GlobalTix at NICE 2026, officiated by the Prime Minister of Malaysia and witnessed by the Minister of Science, Technology and Innovation (MOSTI).',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Press', item: SITE + '/press' },
    { '@type': 'ListItem', position: 3, name: 'StayMii Launches SGMC at NICE 2026', item: SITE + PAGE },
  ],
};

export default function SgmcLaunchPressRelease() {
  return (
    <div className="min-h-screen bg-white" data-testid="sgmc-launch-press-release-page">
      <SEO
        title="StayMii Launches StayMii Global Market Connect (SGMC) to Bring Malaysian Experiences to the World | 27 August 2026"
        description="For immediate release: StayMii launched StayMii Global Market Connect (SGMC) at NICE 2026, KLCC \u2014 officiated by Prime Minister Dato\u2019 Seri Anwar Ibrahim and witnessed by MOSTI Minister Datuk Chang Lih Kang \u2014 to help Malaysian attractions and experiences reach regional and international travellers."
        keywords="StayMii Global Market Connect, SGMC, NICE 2026, KLCC, Anwar Ibrahim, Chang Lih Kang, MOSTI, GlobalTix MOU, Malaysia tourism technology, e-ticketing, attraction distribution"
        canonicalUrl={PAGE}
        ogType="article"
        ogTitle="StayMii Launches StayMii Global Market Connect (SGMC) to Bring Malaysian Experiences to the World"
        ogDescription="StayMii launched SGMC at NICE 2026, KLCC \u2014 officiated by the Prime Minister of Malaysia \u2014 to connect Malaysian attractions and experiences with regional and international tourism markets."
        ogImage={OG_BANNER}
      />
      <StructuredData id="sgmc-launch-press" schema={[articleSchema, eventSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <article className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/press" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-6" data-testid="back-to-press-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All press releases
        </Link>

        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> 27 August 2026</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Kuala Lumpur, Malaysia</span>
          <span className="uppercase tracking-wider text-[10px] text-blue-700 font-semibold">For Immediate Release</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
          StayMii&trade; Launches StayMii Global Market Connect (SGMC) to Bring Malaysian Experiences to the World
        </h1>

        <PressShareBar slug="staymii-launches-sgmc-nice-2026" title="StayMii Launches StayMii Global Market Connect (SGMC) to Bring Malaysian Experiences to the World" />

        <figure className="mb-8 -mx-6 sm:mx-0">
          <img
            src={HERO_IMAGE}
            alt="StayMii Memorandum of Understanding exchange at the launch of StayMii Global Market Connect (SGMC), NICE 2026, KLCC."
            className="w-full aspect-[16/10] object-cover sm:rounded-xl shadow-sm"
            loading="eager"
          />
          <figcaption className="text-xs text-slate-400 mt-2 px-6 sm:px-0">
            The StayMii Memorandum of Understanding exchange at the launch of StayMii Global Market Connect (SGMC), NICE 2026, KLCC.
          </figcaption>
        </figure>

        <div className="prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed"><strong>Kuala Lumpur, 27 August 2026</strong> &mdash; Malaysian tourism and hospitality technology brand StayMii&trade; today launched <strong>StayMii Global Market Connect (SGMC)</strong>, a new initiative aimed at helping Malaysian attractions and experience companies expand their reach into regional and international tourism markets.</p>

          <p>The launch took place in conjunction with a strategic Memorandum of Understanding (MOU) signed during <strong>NICE 2026 at the Kuala Lumpur Convention Centre</strong>, held under the theme <em>&ldquo;Mission to Market: Malaysia to the World.&rdquo;</em></p>

          <div className="not-prose my-8 rounded-xl border border-blue-200 bg-blue-50 p-5 text-sm text-slate-600 space-y-1">
            <p><strong className="text-slate-900">Officiated by:</strong> YAB Dato&rsquo; Seri Anwar bin Ibrahim, Prime Minister of Malaysia</p>
            <p><strong className="text-slate-900">MOU witnessed by:</strong> YB Datuk Chang Lih Kang, Minister of Science, Technology and Innovation (MOSTI)</p>
            <p><strong className="text-slate-900">Event:</strong> NICE 2026 &mdash; National Innovation and Commercialisation Expo &middot; <strong className="text-slate-900">Venue:</strong> KLCC, Kuala Lumpur</p>
          </div>

          <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4">Connecting Malaysian Experiences with Global Markets</h2>
          <p>SGMC strengthens StayMii&rsquo;s role as an e-ticketing platform, providing attractions and experience companies with digital tools to ticket, manage and distribute their products while creating opportunities to reach travellers beyond Malaysia.</p>
          <p>Through StayMii Global Market Connect (SGMC), StayMii will promote and onboard Malaysian attractions, museums and tourism merchants while facilitating their integration with international distribution networks.</p>
          <p>Malaysia has a diverse range of attractions and experiences, from museums and cultural sites to visitor attractions, adventure activities and other unique tourism products. SGMC is designed to help these local experiences become more digitally accessible and better positioned for international distribution.</p>
          <p>StayMii&rsquo;s e-ticketing technology supports digital ticketing, QR-code admission validation, visitor management and operational reporting, providing tourism businesses with the digital infrastructure needed to participate in a connected tourism ecosystem.</p>

          <figure className="my-10 not-prose">
            <img
              src={GROUP_IMAGE}
              alt="StayMii with YB Datuk Chang Lih Kang, Minister of Science, Technology and Innovation (MOSTI), at the launch of StayMii Global Market Connect (SGMC) at NICE 2026, KLCC."
              className="w-full aspect-[16/10] object-cover rounded-xl shadow-sm"
              loading="lazy"
            />
            <figcaption className="text-xs text-slate-400 mt-2">
              StayMii and YB Datuk Chang Lih Kang, Minister of Science, Technology and Innovation (MOSTI) at NICE 2026 following the SGMC launch and MOU signing.
            </figcaption>
          </figure>

          <blockquote className="border-l-4 border-blue-500 pl-4 italic text-slate-700 my-8">
            &ldquo;Tourism digitalisation is more than simply putting tickets online; it is about connecting local experiences with the wider travel ecosystem. Through SGMC, we want to help Malaysian attractions and experiences expand their reach and connect with travellers in regional and international markets.&rdquo;
            <footer className="not-italic text-sm text-slate-500 mt-2">&mdash; Looi Je Dee, Director of Mii Global Partner Holdings Sdn. Bhd. (StayMii)</footer>
          </blockquote>

          <div className="not-prose grid sm:grid-cols-3 gap-4 my-8">
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <Globe2 className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Global reach</p>
                <p className="text-sm text-slate-600">Onboarding and distributing Malaysian attractions to regional and international markets.</p>
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <Ticket className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Digital ticketing</p>
                <p className="text-sm text-slate-600">Sell, manage and distribute tickets from one connected platform.</p>
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <QrCode className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">QR admission</p>
                <p className="text-sm text-slate-600">QR-code validation, visitor management and operational reporting.</p>
              </div>
            </div>
          </div>

          <p>The initiative reflects StayMii&rsquo;s broader vision of supporting Malaysia&rsquo;s tourism industry through technology and digital connectivity, while giving local attractions and experiences greater opportunities to participate in the global travel economy.</p>
          <p>By connecting local tourism products with digital distribution opportunities, StayMii aims to help Malaysian attractions and experiences move beyond domestic visibility and reach travellers across regional and international markets.</p>
          <p>The launch of SGMC represents StayMii&rsquo;s ambition to build a stronger digital pathway for Malaysian tourism &mdash; from local experiences to global markets.</p>

          <div className="not-prose flex flex-wrap gap-3 my-10">
            <Link to="/global-market-connect" className="inline-flex items-center gap-1.5 rounded-full bg-[#0C2159] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#0a1a47] transition-colors" data-testid="sgmc-cta">
              Explore SGMC <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <Link to="/e-ticket" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold px-5 py-2.5 hover:bg-slate-50 transition-colors" data-testid="eticket-cta">
              StayMii E-Ticket <Ticket className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="mt-12 border-t pt-6 text-sm text-slate-500">
            <h3 className="text-sm font-bold text-slate-900 mb-2">About StayMii</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">StayMii is a Malaysian-developed, MySTI-certified tourism and hospitality technology platform providing integrated digital solutions for hotels, serviced apartments, museums, attractions and tourism businesses &mdash; including LHDN-compliant e-invoicing, hospitality management, QR-Code e-ticketing, and digital market distribution through StayMii Global Market Connect (SGMC).</p>
            <p><strong className="text-slate-700">StayMii | Mii Global Partner Holdings Sdn. Bhd.</strong></p>
            <p><strong className="text-slate-700">Media enquiries:</strong> <a href="mailto:media@staymii.com" className="text-blue-600 hover:underline">media@staymii.com</a> &middot; +60 18-271 2281</p>
            <p><strong className="text-slate-700">Website:</strong> <a href="https://www.staymii.com" className="text-blue-600 hover:underline">www.staymii.com</a></p>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed" data-testid="press-trademark-notice">
              <strong>Trademark Notice:</strong> StayMii&trade; is a trademark pending registration with the Intellectual Property Corporation of Malaysia (MyIPO). GlobalTix, NICE and MOSTI are marks of their respective owners.
            </p>
          </div>
        </div>
      </article>

      <LandingFooter />
    </div>
  );
}
